// @flow
import * as React from 'react';
import styled from 'styled-components';
import { imageUrl } from 'utils/filestack';
import Box from 'components/Box';
import { AmpVideo } from 'components/Video';
import { H2, RichText, BodyLarge, Body, HeadingSmall } from 'components/Text';
import { PrimaryButtonLink } from 'components/Button';
import Img, { getSrc } from 'components/Image';
import type { SplitContentBlockData, SplitContentSection } from 'api/cms';
import { gutterWidth } from '../../../theme';

const GifImage = styled(Img)`
  max-width: 100%;
  width: auto;
  @supports (object-fit: contain) {
    object-fit: contain;
    width: 100%;
  }
`;

const ContentContainer = styled(Box)`
  ${({ verticalCenter }) =>
    verticalCenter &&
    `
    margin: auto 0;
  `}
`;

const Content = ({ section }: { section: SplitContentSection }) => {
  switch (section.type) {
    case 'TEXT': {
      return (
        <>
          {section.data.label && (
            <HeadingSmall>{section.data.label}</HeadingSmall>
          )}
          {section.data.headline && (
            <H2 dangerouslySetInnerHTML={{ __html: section.data.headline }} />
          )}
          <RichText dangerouslySetInnerHTML={{ __html: section.data.html }} />
          {section.data.button && (
            <PrimaryButtonLink href={section.data.button.data.url}>
              {section.data.button.text}
            </PrimaryButtonLink>
          )}
        </>
      );
    }
    case 'BLOCKQUOTE':
      return (
        <Box px={[0, null, gutterWidth]}>
          <RichText>
            <Box component="blockquote">
              <BodyLarge
                dangerouslySetInnerHTML={{ __html: section.data.quote }}
              />
              {section.data.cite && (
                <Box mt="18px">
                  <Body>{section.data.cite}</Body>
                </Box>
              )}
            </Box>
          </RichText>
        </Box>
      );
    case 'VIDEO':
      return <AmpVideo url={section.data.url} />;
    case 'IMAGE': {
      const isGif = section.data.image.mimetype === 'image/gif';
      return (
        <Box height="100%">
          {isGif ? (
            <GifImage
              layout="fixed-height"
              height="300"
              src={imageUrl({ handle: section.data.image.handle })}
            />
          ) : (
            <Box
              height="100%"
              minHeight={300}
              borderRadius={!isGif && 24}
              backgroundImage={`url(${getSrc({
                handle: section.data.image.handle
              })})`}
              backgroundSize="cover"
              backgroundPosition="center"
            />
          )}
        </Box>
      );
    }
    default:
      return null;
  }
};

const SplitContentBlock = ({ data }: { data: SplitContentBlockData }) => (
  <Box
    display={['block', null, 'flex']}
    mx={[0, null, -gutterWidth]}
    my={[-gutterWidth / 2, null, 0]}
    width={[1, null, `calc(100% + ${gutterWidth * 2}px)`]}
  >
    {data.sections.map<React.Node>((section, index) => (
      <ContentContainer
        // eslint-disable-next-line react/no-array-index-key
        key={index}
        width={[1, null, 1 / 2]}
        px={[0, null, gutterWidth]}
        py={[gutterWidth / 2, null, 0]}
        verticalCenter={
          section.type === 'IMAGE' &&
          section.data.image.mimetype === 'image/gif'
        }
      >
        <Content section={section} />
      </ContentContainer>
    ))}
  </Box>
);

export default SplitContentBlock;
